import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, ReactNode, useRef, Suspense, lazy, useState } from 'react';
import { useAuthStore } from './store/useAuthStore';
import { useTheme } from './store/useTheme';
import { useSystemStore } from './store/useSystemStore';
import { useDiagnosticStore } from './store/useDiagnosticStore';
import { Toaster } from 'sonner';
import { db, auth, firebaseConfig } from './firebase';
import { BUILD_DATE, BUILD_ID, getConfigDiagnostic } from './config';
import AdminRoute from './components/AdminRoute';
import { GlobalListener } from './components/GlobalListener';
import { BetaOverlay } from './components/BetaOverlay';
import { UpdatePrompt } from './components/UpdatePrompt';
import { FeedbackFloatingButton } from './components/Feedback/FeedbackFloatingButton';
import { ShieldAlert, LogOut, Activity } from 'lucide-react';

function DiagnosticPanel() {
  const { logs, lastError, redirectStatus, googleButtonClicks, clearLogs } = useDiagnosticStore();
  const { loginLoading, isGoogleSigningIn, loading: authLoading, user } = useAuthStore();
  const [show, setShow] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const googleStrategy = isMobile ? 'REDIRECT' : 'POPUP';

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const diagParam = searchParams.get('diag') === 'true';
    
    const isBootstrapAdmin = user?.email === 'juansolisc@gmail.com' || auth.currentUser?.email === 'juansolisc@gmail.com';
    const isAdmin = user?.role === 'admin' || isBootstrapAdmin;
    const isDeveloper = user?.role === 'developer' || isAdmin;
    const isBeta = user?.badges?.includes('beta') || user?.badges?.includes('tester') || user?.plan === 'interna';
    
    const debugForced = localStorage.getItem('debug_log_visible') === 'true';
    
    if (diagParam || (isDeveloper && debugForced) || (isBeta && debugForced)) {
      setIsVisible(true);
      if (diagParam) setShow(true);
    } else {
      setIsVisible(false);
      setShow(false);
    }
  }, [user]);

  useEffect(() => {
    const handleStorage = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const diagParam = searchParams.get('diag') === 'true';
      const isBootstrapAdmin = user?.email === 'juansolisc@gmail.com' || auth.currentUser?.email === 'juansolisc@gmail.com';
      const isAdmin = user?.role === 'admin' || isBootstrapAdmin;
      const isDeveloper = user?.role === 'developer' || isAdmin;
      const isBeta = user?.badges?.includes('beta') || user?.badges?.includes('tester') || user?.plan === 'interna';
      const debugForced = localStorage.getItem('debug_log_visible') === 'true';
      
      if (diagParam || (isDeveloper && debugForced) || (isBeta && debugForced)) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setShow(false);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user]);

  if (!isVisible) return null;

  if (!show) return (
    <button 
      onClick={() => setShow(true)}
      className="fixed bottom-4 right-4 bg-zinc-800/80 text-white p-2 rounded-full shadow-lg z-[9999] hover:bg-zinc-700 transition-colors"
      title="Open Diagnostics"
    >
      <Activity size={16} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-[9999] bg-zinc-950 text-[10px] font-mono text-emerald-400 p-4 overflow-y-auto selection:bg-emerald-500/30">
      <div className="flex justify-between items-center mb-6 border-b border-emerald-500/30 pb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} />
          <h3 className="font-black uppercase tracking-widest text-xs">Diagnostic System v1.2</h3>
        </div>
        <div className="flex gap-2">
          <button onClick={clearLogs} className="bg-zinc-800 hover:bg-zinc-700 text-white font-black px-3 py-1.5 rounded-xl uppercase tracking-widest transition-colors">Clear</button>
          <button onClick={() => setShow(false)} className="bg-rose-600 hover:bg-rose-500 text-white font-black px-3 py-1.5 rounded-xl uppercase tracking-widest transition-colors">Close</button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="space-y-1.5 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
          <p className="opacity-50 uppercase text-[8px] font-black mb-1">Environment</p>
          <p><span className="text-emerald-200/50">Origin:</span> {window.location.origin}</p>
          <p><span className="text-emerald-200/50">Auth Domain:</span> <span className="text-emerald-300 font-bold">{firebaseConfig.authDomain}</span></p>
          <p><span className="text-emerald-200/50">Path:</span> {window.location.pathname}</p>
          <p><span className="text-emerald-200/50">Referrer:</span> {document.referrer || 'none'}</p>
          <p className="break-all"><span className="text-emerald-200/50">UA:</span> {navigator.userAgent}</p>
        </div>
        <div className="space-y-1.5 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
          <p className="opacity-50 uppercase text-[8px] font-black mb-1">Auth & Build State</p>
          <p><span className="text-emerald-200/50">Auth Ready:</span> {String(!authLoading)}</p>
          <p><span className="text-emerald-200/50">Auth Init:</span> {String(useAuthStore.getState().authInitialized)}</p>
          <p><span className="text-emerald-200/50">Google Strategy:</span> <span className="font-black text-emerald-300">{googleStrategy}</span></p>
          <p><span className="text-emerald-200/50">Build Date:</span> <span className="text-[10px] font-mono text-emerald-100 font-bold">{BUILD_DATE}</span></p>
          <p><span className="text-emerald-200/50">Build ID:</span> <span className="text-[10px] font-mono text-emerald-300 font-black">{BUILD_ID}</span></p>
          {(() => {
            const diag = getConfigDiagnostic();
            return (
              <div className="mt-2 pt-2 border-t border-emerald-500/10 space-y-1">
                <p><span className="text-emerald-200/50">Standalone/PWA:</span> <span className="text-emerald-300/70">{String(diag.isPWA)}</span></p>
                
                <button 
                  onClick={() => {
                     if (window.confirm('¿Quieres limpiar TODA la caché y reiniciar? Esto soluciona problemas de versiones antiguas en móviles.')) {
                       window.location.href = window.location.origin + '/?clear=true';
                     }
                  }}
                  className="w-full mt-2 text-[8px] bg-rose-500/20 text-rose-300 px-2 py-1.5 rounded-xl border border-rose-500/30 hover:bg-rose-500/40 transition-colors uppercase font-black"
                >
                  Force Cache Refresh (Update App)
                </button>
              </div>
            );
          })()}
          <p className="pt-2"><span className="text-emerald-200/50">Redirect Status:</span> 
            <span className={`ml-2 px-1.5 py-0.5 rounded ${
              redirectStatus === 'OK' ? 'bg-emerald-500 text-zinc-950 font-black' : 
              redirectStatus === 'ERROR' ? 'bg-rose-500 text-zinc-950 font-black' : 'bg-zinc-800'
            }`}>
              {redirectStatus}
            </span>
          </p>
        </div>
      </div>

      {lastError && (
        <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-400">
          <p className="font-black uppercase text-[8px] mb-2 opacity-70">Critical Last Error</p>
          <p className="font-bold">Code: {lastError.code}</p>
          <p className="mt-1 leading-relaxed">{lastError.message}</p>
        </div>
      )}

      <div className="border border-emerald-500/20 p-4 rounded-2xl bg-zinc-900/50">
        <p className="font-black uppercase tracking-widest text-[8px] border-b border-emerald-500/10 mb-3 pb-2 opacity-50">Timeline Events</p>
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="flex gap-3 items-start animate-in fade-in slide-in-from-left-2 duration-300">
              <span className="text-emerald-500/40 shrink-0 tabular-nums">[{log.timestamp}]</span>
              <div className="flex flex-col gap-0.5">
                <span className="font-black uppercase tracking-tighter text-emerald-300">{log.step}</span>
                {log.data && (
                  <span className="text-zinc-500 break-all leading-tight">
                    {typeof log.data === 'object' ? JSON.stringify(log.data) : String(log.data)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Critical pages loaded statically to avoid Suspense issues during initial boot
import Home from './pages/Home';
import Auth from './pages/Auth';

const Game = lazy(() => import('./pages/Game'));
const Settings = lazy(() => import('./pages/Settings'));
const GameSettings = lazy(() => import('./pages/GameSettings'));
const AlertsSettings = lazy(() => import('./pages/AlertsSettings'));
const PrivacySettings = lazy(() => import('./pages/PrivacySettings'));
const SecuritySettings = lazy(() => import('./pages/SecuritySettings'));
const LegalRules = lazy(() => import('./pages/LegalRules'));
const Help = lazy(() => import('./pages/Help'));
const SetupGame = lazy(() => import('./pages/SetupGame'));
const BoardEditor = lazy(() => import('./pages/BoardEditor'));
const PlayerProfileScreen = lazy(() => import('./pages/PlayerProfileScreen'));
const ProfileScreen = lazy(() => import('./pages/ProfileScreen'));
const HeadToHeadScreen = lazy(() => import('./pages/HeadToHeadScreen'));
const DeveloperScreen = lazy(() => import('./pages/DeveloperScreen'));
const AchievementsScreen = lazy(() => import('./pages/AchievementsScreen'));
const RankingsScreen = lazy(() => import('./pages/RankingsScreen'));
const UserControl = lazy(() => import('./pages/UserControl'));
const FeedbackAdmin = lazy(() => import('./pages/FeedbackAdmin'));
const AdminLexicography = lazy(() => import('./pages/Admin/Lexicography/LexicographyRoot'));

function ScreenLoader({ label }: { label?: string }) {
  const renderCountRef = useRef(0);
  renderCountRef.current++;
  
  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center text-text-secondary flex-col gap-4 p-6 text-center">
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 border-4 border-primary-accent border-t-transparent rounded-full animate-spin mb-2" />
        <div className="font-black text-sm uppercase tracking-[0.2em] animate-pulse">
          {label || "Cargando..." }
        </div>
      </div>
      
      {/* Troubleshooting info shown only if it takes too long */}
      <div className="mt-8 text-[10px] opacity-40 font-mono space-y-1">
        <p>Render: {renderCountRef.current}</p>
        <p>Time: {((Date.now() - (window as any)._appStartTime || 0) / 1000).toFixed(1)}s</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);
  const { config, configLoaded } = useSystemStore();

  if (loading || !configLoaded) return <ScreenLoader label="Verificando sesión..." />;
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Beta access check
  const isDeveloper = user.role === 'developer' || user.role === 'admin' || user.email === 'juansolisc@gmail.com';
  
  if (config.betaEnabled && config.betaRequireAllowlist && !isDeveloper) {
    const isAllowed = config.betaAllowlist.some(email => 
      email.toLowerCase() === user.email?.toLowerCase()
    );

    if (!isAllowed) {
      return (
        <div className="min-h-screen bg-app-bg flex items-center justify-center p-6 w-full">
          <div className="max-w-md w-full bg-card-bg border border-grid-line shadow-2xl rounded-[2.5rem] p-8 text-center space-y-8">
            <div className="w-20 h-20 bg-rose-500/10 text-rose-500 rounded-[2rem] flex items-center justify-center mx-auto border border-rose-500/20">
              <ShieldAlert size={40} />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-black text-text-primary tracking-tight uppercase">Acceso Restringido</h1>
              <p className="text-sm text-text-secondary font-medium leading-relaxed">
                Lo sentimos, <span className="text-text-primary font-bold">{user.email}</span> no está en la lista de acceso para esta fase beta cerrada.
              </p>
            </div>

            <div className="bg-board-bg/50 border border-grid-line p-4 rounded-2xl text-[10px] text-text-muted font-bold uppercase tracking-widest leading-relaxed">
              Si crees que esto es un error, contacta con el administrador del sistema.
            </div>

            <button
              onClick={() => useAuthStore.getState().logout()}
              className="w-full py-4.5 bg-grid-line/20 text-text-primary border border-grid-line rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-grid-line/30 transition-all uppercase text-[10px] tracking-widest"
            >
              <LogOut size={14} />
              Cerrar Sesión
            </button>
          </div>
        </div>
      );
    }
  }

  return (
    <>
      {config.betaEnabled && <BetaOverlay message={config.betaMessage} onAccept={() => {}} />}
      {children}
    </>
  );
}

export default function App() {
  const initAuth = useAuthStore((state) => state.initAuth);
  const syncSystem = useSystemStore((state) => state.syncWithFirestore);
  const theme = useTheme((state) => state.theme);
  const diag = useDiagnosticStore();

  const authStartedRef = useRef(false);

  useEffect(() => {
    diag.loadFromSession();
    diag.addLog('APP_START');
    diag.addLog('FIREBASE_CONFIG_CHECK', { hasDb: !!db, hasAuth: !!auth });
    
    if (authStartedRef.current) return;
    authStartedRef.current = true;

    diag.addLog('APP_START_INITIALIZING');
    const unsubscribeAuth = initAuth();
    const unsubscribeSystem = syncSystem();

    diag.addLog('SUBSCRIPTIONS_ACTIVE');

    return () => {
      if (typeof unsubscribeAuth === 'function') {
        unsubscribeAuth();
      }
      if (typeof unsubscribeSystem === 'function') {
        unsubscribeSystem();
      }
    };
  }, [initAuth, syncSystem]);

  useEffect(() => {
    if (useAuthStore.getState().user) {
      diag.addLog('APP_RENDER_READY_AUTH');
    } else {
      diag.addLog('APP_RENDER_READY_GUEST');
    }
  }, []);

  useEffect(() => {
    const themeClasses = ['theme-warm', 'theme-vintage', 'theme-premium', 'theme-chat-1', 'theme-playful', 'theme-clean-compact', 'theme-default-height-first', 'theme-clean-blue-contrast', 'theme-mono-premium', 'dark'];
    document.documentElement.classList.remove(...themeClasses);
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);

  return (
    <Router>
      <div className="min-h-screen bg-app-bg text-text-primary font-sans selection:bg-primary-accent/30 flex justify-center">
        <DiagnosticPanel />
        <UpdatePrompt />
        <div className="w-full max-w-[1080px] min-h-screen bg-app-bg relative overflow-hidden shadow-card border-x border-grid-line/50">
          <Toaster position="top-center" richColors theme="light" />
          <GlobalListener />
          <FeedbackFloatingButton />

          <Suspense fallback={<ScreenLoader label="Cargando módulo..." />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/setup"
                element={
                  <ProtectedRoute>
                    <SetupGame />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/board-editor/:boardId?"
                element={
                  <ProtectedRoute>
                    <BoardEditor />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
 
              <Route
                path="/settings/game"
                element={
                  <ProtectedRoute>
                    <GameSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/alerts"
                element={
                  <ProtectedRoute>
                    <AlertsSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/privacy"
                element={
                  <ProtectedRoute>
                    <PrivacySettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/security"
                element={
                  <ProtectedRoute>
                    <SecuritySettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/legal"
                element={
                  <ProtectedRoute>
                    <LegalRules />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/help"
                element={
                  <ProtectedRoute>
                    <Help />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/game/:gameId"
                element={
                  <ProtectedRoute>
                    <Game />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/player/:userId"
                element={
                  <ProtectedRoute>
                    <PlayerProfileScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/head-to-head/:userId"
                element={
                  <ProtectedRoute>
                    <HeadToHeadScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/developer"
                element={
                  <AdminRoute>
                    <DeveloperScreen />
                  </AdminRoute>
                }
              />

              <Route
                path="/achievements"
                element={
                  <ProtectedRoute>
                    <AchievementsScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/rankings"
                element={
                  <ProtectedRoute>
                    <RankingsScreen />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <UserControl />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/feedback"
                element={
                  <AdminRoute>
                    <FeedbackAdmin />
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/lexicography"
                element={
                  <AdminRoute>
                    <AdminLexicography />
                  </AdminRoute>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </div>
    </Router>
  );
}
